import type { ComponentType } from "@zirro/schema";

/**
 * Inspector field descriptors for every component (PRD §27, §29–34).
 * The editor renders controls from this table — it never hardcodes
 * per-component inspector layouts.
 */
export type InspectorControl =
  | "text"
  | "textarea"
  | "richtext"
  | "select"
  | "segmented"
  | "number"
  | "toggle"
  | "color"
  | "link"
  | "image"
  | "list";

export interface InspectorField {
  prop: string;
  label: string;
  control: InspectorControl;
  group: "content" | "style" | "layout";
  options?: { value: string | number; label: string }[];
  help?: string;
}

const SPACING_OPTIONS = ["none", "XS", "SM", "MD", "LG", "XL"].map((value) => ({
  value,
  label: value,
}));
const FONT_SIZE_OPTIONS = ["sm", "base", "lg", "xl", "2xl", "3xl", "4xl", "5xl"].map(
  (value) => ({ value, label: value }),
);
const COLOR_OPTIONS = [
  "primary",
  "secondary",
  "accent",
  "background",
  "surface",
  "text",
  "muted",
  "border",
].map((value) => ({ value, label: value }));
const VISIBILITY_OPTIONS = [
  { value: "all", label: "All devices" },
  { value: "desktop-only", label: "Desktop only" },
  { value: "mobile-only", label: "Mobile only" },
];

const LAYOUT_FIELDS: InspectorField[] = [
  {
    prop: "direction",
    label: "Direction",
    control: "segmented",
    group: "layout",
    options: [
      { value: "vertical", label: "Stack" },
      { value: "horizontal", label: "Row" },
    ],
  },
  {
    prop: "align",
    label: "Align",
    control: "segmented",
    group: "layout",
    options: [
      { value: "start", label: "Start" },
      { value: "center", label: "Center" },
      { value: "end", label: "End" },
      { value: "stretch", label: "Stretch" },
    ],
  },
  {
    prop: "justify",
    label: "Justify",
    control: "segmented",
    group: "layout",
    options: [
      { value: "start", label: "Start" },
      { value: "center", label: "Center" },
      { value: "end", label: "End" },
      { value: "between", label: "Between" },
    ],
  },
  { prop: "columns", label: "Columns", control: "number", group: "layout" },
  { prop: "gap", label: "Gap", control: "select", group: "layout", options: SPACING_OPTIONS },
  {
    prop: "padding",
    label: "Padding",
    control: "select",
    group: "layout",
    options: SPACING_OPTIONS,
  },
  {
    prop: "visibility",
    label: "Visibility",
    control: "select",
    group: "layout",
    options: VISIBILITY_OPTIONS,
  },
];

const TEXT_ALIGN_OPTIONS = [
  { value: "left", label: "Left" },
  { value: "center", label: "Center" },
  { value: "right", label: "Right" },
];

export const INSPECTOR_FIELDS: Record<ComponentType, InspectorField[]> = {
  Section: [
    ...LAYOUT_FIELDS,
    {
      prop: "background",
      label: "Background",
      control: "color",
      group: "style",
      options: COLOR_OPTIONS,
    },
    {
      prop: "width",
      label: "Width",
      control: "segmented",
      group: "layout",
      options: [
        { value: "contained", label: "Contained" },
        { value: "full", label: "Full" },
      ],
    },
  ],
  Container: LAYOUT_FIELDS,
  Heading: [
    { prop: "text", label: "Text", control: "text", group: "content" },
    {
      prop: "level",
      label: "Level",
      control: "select",
      group: "content",
      options: [1, 2, 3, 4, 5, 6].map((value) => ({
        value,
        label: `H${value}`,
      })),
      help: "Heading level for structure and accessibility",
    },
    { prop: "align", label: "Align", control: "segmented", group: "style", options: TEXT_ALIGN_OPTIONS },
    { prop: "color", label: "Color", control: "color", group: "style", options: COLOR_OPTIONS },
    {
      prop: "fontSize",
      label: "Size",
      control: "select",
      group: "style",
      options: FONT_SIZE_OPTIONS,
    },
    {
      prop: "weight",
      label: "Weight",
      control: "select",
      group: "style",
      options: [400, 500, 600, 700, 800].map((value) => ({
        value,
        label: String(value),
      })),
    },
    {
      prop: "visibility",
      label: "Visibility",
      control: "select",
      group: "layout",
      options: VISIBILITY_OPTIONS,
    },
  ],
  Text: [
    { prop: "content", label: "Text", control: "richtext", group: "content" },
    { prop: "align", label: "Align", control: "segmented", group: "style", options: TEXT_ALIGN_OPTIONS },
    { prop: "color", label: "Color", control: "color", group: "style", options: COLOR_OPTIONS },
    {
      prop: "size",
      label: "Size",
      control: "select",
      group: "style",
      options: FONT_SIZE_OPTIONS,
    },
    {
      prop: "visibility",
      label: "Visibility",
      control: "select",
      group: "layout",
      options: VISIBILITY_OPTIONS,
    },
  ],
  Image: [
    { prop: "src", label: "Image", control: "image", group: "content" },
    { prop: "alt", label: "Alt text", control: "text", group: "content", help: "Describes the image for screen readers" },
    {
      prop: "width",
      label: "Width",
      control: "segmented",
      group: "style",
      options: [
        { value: "auto", label: "Auto" },
        { value: "full", label: "Full" },
      ],
    },
    {
      prop: "align",
      label: "Align",
      control: "segmented",
      group: "style",
      options: TEXT_ALIGN_OPTIONS,
    },
    { prop: "rounded", label: "Rounded", control: "toggle", group: "style" },
    {
      prop: "visibility",
      label: "Visibility",
      control: "select",
      group: "layout",
      options: VISIBILITY_OPTIONS,
    },
  ],
  Button: [
    { prop: "label", label: "Label", control: "text", group: "content" },
    { prop: "href", label: "Link", control: "link", group: "content" },
    {
      prop: "variant",
      label: "Variant",
      control: "select",
      group: "style",
      options: [
        { value: "primary", label: "Primary" },
        { value: "secondary", label: "Secondary" },
        { value: "outline", label: "Outline" },
        { value: "ghost", label: "Ghost" },
      ],
    },
    {
      prop: "size",
      label: "Size",
      control: "select",
      group: "style",
      options: [
        { value: "sm", label: "Small" },
        { value: "md", label: "Medium" },
        { value: "lg", label: "Large" },
      ],
    },
    {
      prop: "align",
      label: "Align",
      control: "segmented",
      group: "style",
      options: TEXT_ALIGN_OPTIONS,
    },
    {
      prop: "visibility",
      label: "Visibility",
      control: "select",
      group: "layout",
      options: VISIBILITY_OPTIONS,
    },
  ],
  Header: [
    { prop: "logoText", label: "Logo text", control: "text", group: "content" },
    { prop: "links", label: "Navigation links", control: "list", group: "content" },
    { prop: "cta", label: "Call to action", control: "link", group: "content" },
  ],
  Footer: [
    { prop: "businessName", label: "Business name", control: "text", group: "content" },
    { prop: "links", label: "Links", control: "list", group: "content" },
    { prop: "email", label: "Email", control: "text", group: "content" },
    { prop: "phone", label: "Phone", control: "text", group: "content" },
    { prop: "copyright", label: "Copyright", control: "text", group: "content" },
  ],
  Card: [
    { prop: "title", label: "Title", control: "text", group: "content" },
    { prop: "description", label: "Description", control: "textarea", group: "content" },
    { prop: "variant", label: "Variant", control: "select", group: "style", options: [{ value: "surface", label: "Surface" }, { value: "primary", label: "Primary" }] },
    { prop: "padding", label: "Padding", control: "select", group: "style", options: SPACING_OPTIONS },
    { prop: "align", label: "Align", control: "segmented", group: "style", options: TEXT_ALIGN_OPTIONS },
  ],
  FeatureGrid: [
    { prop: "columns", label: "Columns", control: "number", group: "layout" },
    { prop: "gap", label: "Gap", control: "select", group: "layout", options: SPACING_OPTIONS },
  ],
  Badge: [
    { prop: "text", label: "Text", control: "text", group: "content" },
    { prop: "variant", label: "Variant", control: "select", group: "style", options: [{ value: "primary", label: "Primary" }, { value: "accent", label: "Accent" }] },
    { prop: "size", label: "Size", control: "select", group: "style", options: [{ value: "sm", label: "Small" }, { value: "lg", label: "Large" }] },
  ],
  Divider: [
    { prop: "color", label: "Color", control: "color", group: "style", options: COLOR_OPTIONS },
  ],
  Spacer: [
    { prop: "height", label: "Height", control: "select", group: "layout", options: SPACING_OPTIONS },
  ],
};
